import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { Building2, Eye, List, Map as MapIcon, MapPin, Pencil, Plus, RotateCcw, Search, Trash2, Zap } from 'lucide-react';
import { lazy, Suspense, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useChargers, useRejectCharger, useRemovedChargers, useUpdateCharger, type Charger } from '../../api/chargers';
import { ApiError } from '../../api/client';
import { useSites, type Site } from '../../api/organisations';
import { useMe } from '../../api/auth';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { RowMenu } from '../../components/RowMenu';
import { StatusLabel } from '../../components/StatusLabel';
import { useToast } from '../../components/Toast';
import { formatAgo, formatDate } from '../../lib/format';
import { placeOf, siteLabels } from '../../lib/place';
import { formatRate } from '@pulsepoint/shared';
import { colours, radius } from '../../theme';
import { AcceptChargerDialog } from './AcceptChargerDialog';
import { ChargerDialog } from './ChargerDialog';
import { ConnectorStatuses } from './ConnectorStatuses';
import { chargerName, isCharging, kw, kwh, needsAttention } from './status';
import { useCommand } from './useCommand';
import { useRemoveChargerFlow } from './useRemoveCharger';
import { SiteDialog } from '../organisations/SiteDialog';

// The map library is large: loaded only when someone opens the Map view.
const SitesMap = lazy(() => import('./SitesMap').then((m) => ({ default: m.SitesMap })));

type View = 'list' | 'map';
const VIEW_KEY = 'pp-sites-view';
function savedView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'map';
  } catch {
    return 'map';
  }
}

type Filter = 'all' | 'available' | 'charging' | 'attention';
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Every site and its chargers, live. New chargers wait at the top for an admin. */
export function SitesPage() {
  const chargers = useChargers();
  const sites = useSites(undefined);
  const { data: me } = useMe();
  const canRegister = usePermission('chargers.register');
  const dialog = useDialog();
  const [query, setQuery] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');
  const [filter, setFilter] = useState<Filter>('all');
  // Map (the default) or List, remembered on this browser.
  const [view, setViewState] = useState<View>(savedView);
  const setView = (v: View) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* private browsing: just not remembered */
    }
  };
  const canEditSites = usePermission('sites.edit');

  const all = chargers.data ?? [];
  const accepted = all.filter((c) => c.registration === 'accepted' && c.siteId);
  const waiting = all.filter((c) => c.registration === 'pending' || !c.siteId);
  const attention = accepted.filter(needsAttention);
  const manyOrgs = me?.role === 'admin' || (me?.organisationIds?.length ?? 0) > 1;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return accepted.filter((c) => {
      if (siteFilter !== 'all' && c.siteId !== siteFilter) return false;
      if (filter === 'charging' && !isCharging(c)) return false;
      if (filter === 'attention' && !needsAttention(c)) return false;
      if (filter === 'available' && (needsAttention(c) || isCharging(c))) return false;
      return !q || [c.label, c.chargePointId, c.siteName, c.organisationName, c.bay, c.vendor, c.model].some((v) => v?.toLowerCase().includes(q));
    });
  }, [accepted, query, siteFilter, filter]);

  const bySite = useMemo(() => {
    const groups = new Map<string, Charger[]>();
    for (const c of shown) groups.set(c.siteId!, [...(groups.get(c.siteId!) ?? []), c]);
    return [...groups.entries()]
      .map(([siteId, list]) => {
        const first = list[0]!;
        const single = first.organisationSiteCount <= 1;
        // One site: the organisation is the heading. Several: the site, with its organisation.
        return { siteId, heading: placeOf(first), sub: single ? null : manyOrgs ? first.organisationName : null, list };
      })
      .sort((a, b) => a.heading.localeCompare(b.heading, 'en-AU'));
  }, [shown]);

  // The Map view: each site with its chargers (as filtered), and those with no location yet.
  const mapSites = useMemo(() => {
    const byId = new Map<string, Site>((sites.data ?? []).map((s) => [s.id, s]));
    return bySite.flatMap((g) => {
      const site = byId.get(g.siteId);
      return site ? [{ site, heading: g.heading, sub: g.sub, chargers: g.list }] : [];
    });
  }, [bySite, sites.data]);
  // Off the map: neither the site nor any of its chargers has a location.
  const unplaced = mapSites.filter((m) => !(m.site.latitude && m.site.longitude) && !m.chargers.some((c) => c.latitude && c.longitude));

  const siteCount = new Set(accepted.map((c) => c.siteId)).size;
  const emptySites = (sites.data ?? []).filter((s) => !accepted.some((c) => c.siteId === s.id));
  const labels = siteLabels(sites.data ?? [], manyOrgs);
  const canOpen = usePermission('chargers.details');
  const open = (c: Charger) => void (canOpen && dialog.open(ChargerDialog, { chargerId: c.id }));

  const lead = chargers.isPending
    ? ' '
    : accepted.length === 0
      ? 'No chargers yet.'
      : `${plural(accepted.length, 'charger')} in ${plural(siteCount, 'place')}. ${attention.length ? `${attention.length} ${attention.length === 1 ? 'needs' : 'need'} attention.` : 'All are working.'}`;

  return (
    <>
      <PageHeader
        title="Sites and chargers."
        lead={lead}
        actions={canRegister && <Button variant="contained" startIcon={<Plus size={20} strokeWidth={1.75} />} onClick={() => void dialog.open(AcceptChargerDialog, {})}>Add a charger</Button>}
      />

      {canRegister && waiting.length > 0 && <WaitingChargers chargers={waiting} />}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 4, alignItems: { md: 'center' } }}>
        <TextField
          size="small"
          placeholder="Search chargers, sites or bays"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: { xs: '100%', md: 300 } }}
          slotProps={{ htmlInput: { 'aria-label': 'Search chargers' }, input: { startAdornment: <InputAdornment position="start"><Search size={18} strokeWidth={1.5} /></InputAdornment> } }}
        />
        <TextField select size="small" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} sx={{ width: { xs: '100%', md: 240 } }} slotProps={{ htmlInput: { 'aria-label': 'Where' } }}>
          <MenuItem value="all">Everywhere ({plural(siteCount, 'place')})</MenuItem>
          {(sites.data ?? [])
            .filter((s) => accepted.some((c) => c.siteId === s.id))
            .sort((a, b) => (labels.get(a.id) ?? '').localeCompare(labels.get(b.id) ?? '', 'en-AU'))
            .map((s) => (
              <MenuItem key={s.id} value={s.id}>{labels.get(s.id) ?? s.name}</MenuItem>
            ))}
        </TextField>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_e, v: View | null) => v && setView(v)}
          aria-label="View"
          sx={{ order: { md: 9 }, ml: { md: 'auto !important' }, alignSelf: { xs: 'flex-start', md: 'center' } }}
        >
          <ToggleButton value="map" sx={{ px: 1.75, gap: 0.75 }}><MapIcon size={16} strokeWidth={1.75} /> Map</ToggleButton>
          <ToggleButton value="list" sx={{ px: 1.75, gap: 0.75 }}><List size={16} strokeWidth={1.75} /> List</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }} role="group" aria-label="Show">
          {(
            [
              ['all', 'All'],
              ['available', 'Available'],
              ['charging', 'Charging'],
              ['attention', `Needs attention${attention.length ? ` (${attention.length})` : ''}`],
            ] as [Filter, string][]
          ).map(([f, label]) => (
            <Chip
              key={f}
              label={label}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              sx={filter === f ? { bgcolor: colours.nearBlack, color: colours.white, '&:hover': { bgcolor: colours.nearBlack } } : { bgcolor: colours.white, border: `1px solid ${colours.hairline}` }}
            />
          ))}
        </Stack>
      </Stack>

      {chargers.isPending ? (
        <Skeleton variant="rounded" height={240} />
      ) : chargers.error ? (
        <Typography color="error">{chargers.error.message}</Typography>
      ) : accepted.length === 0 ? (
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 } }}>
          <EmptyState title="No chargers yet">
            A charger appears here once it connects to Pulsepoint and an admin accepts it. Point it at wss://ocpp.infinityev.com.au with its charge point ID.
          </EmptyState>
        </Paper>
      ) : view === 'map' ? (
        <>
          <Suspense fallback={<Skeleton variant="rounded" height={480} />}>
            <SitesMap sites={mapSites} canOpen={canOpen} onOpen={open} />
          </Suspense>
          {unplaced.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <Typography color="textSecondary">
                {unplaced.length === 1 ? '1 site isn’t on the map: it has no location.' : `${unplaced.length} sites aren’t on the map: they have no location.`}
                {canEditSites ? ' Add it from the address:' : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {unplaced.map((m) =>
                  canEditSites ? (
                    <Button
                      key={m.site.id}
                      size="small"
                      startIcon={<MapPin size={16} strokeWidth={1.75} />}
                      onClick={() => void dialog.open(SiteDialog, { organisationId: m.site.organisationId, organisationName: m.site.organisationName, organisationTimezone: m.site.timezone, site: m.site })}
                    >
                      {m.heading}
                    </Button>
                  ) : (
                    <Typography key={m.site.id} variant="body2">{m.heading}</Typography>
                  ),
                )}
              </Box>
            </Box>
          )}
        </>
      ) : bySite.length === 0 ? (
        <Typography color="textSecondary">No chargers match. <Button variant="text" onClick={() => { setQuery(''); setFilter('all'); setSiteFilter('all'); }}>Show all</Button></Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 6 }}>
          {bySite.map((g) => (
            <Box component="section" key={g.siteId} aria-labelledby={`site-${g.siteId}`}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                {(() => {
                  const site = canEditSites ? (sites.data ?? []).find((x) => x.id === g.siteId) : undefined;
                  if (!site) return <Typography variant="h2" id={`site-${g.siteId}`}>{g.heading}</Typography>;
                  // Admins and managers: the site's name opens the site window.
                  return (
                    <Typography variant="h2" id={`site-${g.siteId}`}>
                      <ButtonBase
                        onClick={() => void dialog.open(SiteDialog, { organisationId: site.organisationId, organisationName: site.organisationName, organisationTimezone: site.timezone, site })}
                        title={`Edit ${g.heading}`}
                        sx={{ font: 'inherit', color: 'inherit', textAlign: 'left', borderRadius: `${radius.button}px`, textUnderlineOffset: 4, '&:hover': { textDecoration: 'underline' } }}
                      >
                        {g.heading}
                      </ButtonBase>
                    </Typography>
                  );
                })()}
                <Typography color="textSecondary">
                  {[g.sub, plural(g.list.length, 'charger')].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <ChargerTable chargers={g.list} onOpen={open} />
            </Box>
          ))}
        </Box>
      )}

      {!chargers.isPending && emptySites.length > 0 && filter === 'all' && !query && siteFilter === 'all' && (
        <Typography color="textSecondary" sx={{ mt: 5 }}>
          No chargers yet at {emptySites.map((s) => labels.get(s.id) ?? s.name).join(', ')}.{' '}
          <Box component={RouterLink} to="/settings/organisations">Organisations and sites</Box>
        </Typography>
      )}

      {canRegister && <RemovedChargers />}
    </>
  );
}

/** Chargers that were removed, hidden until asked for, each with Restore (admins). */
function RemovedChargers() {
  const [shown, setShown] = useState(false);
  const removed = useRemovedChargers(true);
  const update = useUpdateCharger();
  const toast = useToast();
  const list = removed.data ?? [];
  if (!list.length) return null;
  return (
    <Box sx={{ mt: 6 }}>
      <Button variant="text" onClick={() => setShown(!shown)} aria-expanded={shown} sx={{ px: 0, color: colours.graphite }}>
        {shown ? 'Hide removed chargers' : `Show removed chargers (${list.length})`}
      </Button>
      {shown && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 1, p: 0, borderTop: `1px solid ${colours.hairline}` }}>
          {list.map((c) => (
            <Box component="li" key={c.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1.5, borderBottom: `1px solid ${colours.hairline}` }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500 }}>{chargerName(c)}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {[c.label ? c.chargePointId : null, c.siteId ? placeOf(c) : null, c.lastSeenAt ? `last heard ${formatAgo(c.lastSeenAt)}` : null].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Button
                size="small"
                disabled={update.isPending}
                onClick={async () => {
                  await update.mutateAsync({ id: c.id, isActive: true });
                  toast(`${chargerName(c)} was restored.`);
                }}
              >
                Restore
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function ChargerTable({ chargers, onOpen }: { chargers: Charger[]; onOpen(c: Charger): void }) {
  const canOpen = usePermission('chargers.details');
  const columns: Column<Charger>[] = [
    {
      key: 'name',
      header: 'Charger',
      sortValue: (c) => chargerName(c).toLowerCase(),
      render: (c) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{chargerName(c)}</Typography>
          <Typography variant="body2" color="textSecondary">
            {[c.label ? c.chargePointId : null, [c.vendor, c.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      ),
    },
    { key: 'bay', header: 'Bay', hideBelow: 'md', sortValue: (c) => c.bay, render: (c) => c.bay ?? '—' },
    { key: 'status', header: 'Status', render: (c) => <ConnectorStatuses charger={c} /> },
    {
      key: 'price',
      header: 'Price',
      hideBelow: 'md',
      sortValue: (c) => c.tariff?.pricePerKwh ?? null,
      render: (c) =>
        c.tariff ? (
          <Box>
            <Typography className="num">{formatRate(c.tariff.pricePerKwh)}/kWh</Typography>
            <Typography variant="body2" color="textSecondary">{c.publicCharging ? (c.nextTariff ? `${formatRate(c.nextTariff.pricePerKwh)} from ${formatDate(c.nextTariff.validFrom)}` : 'Public charging') : 'Public charging off'}</Typography>
          </Box>
        ) : (
          <Typography color="textSecondary">—</Typography>
        ),
    },
    {
      key: 'now',
      header: 'Now',
      hideBelow: 'sm',
      render: (c) =>
        c.activeSessions.length ? (
          <Box className="num">
            {c.activeSessions.map((s) => (
              <Typography key={s.id}>
                {[s.powerW ? kw(s.powerW) : null, kwh(s.energyWh)].filter(Boolean).join(' · ')}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography color="textSecondary">{c.isConnected ? '—' : c.lastSeenAt ? `Last heard ${formatAgo(c.lastSeenAt)}` : 'Never connected'}</Typography>
        ),
    },
  ];
  return (
    <DataTable
      label="Chargers"
      rows={chargers}
      columns={columns}
      rowKey={(c) => c.id}
      initialSort={{ key: 'name', direction: 'asc' }}
      pageSize={100}
      onRowClick={canOpen ? onOpen : undefined}
      rowActions={canOpen ? (c) => <ChargerMenu charger={c} onOpen={() => onOpen(c)} /> : undefined}
      empty={null}
    />
  );
}

function ChargerMenu({ charger: c, onOpen }: { charger: Charger; onOpen(): void }) {
  const canCommand = usePermission('chargers.command');
  const canQuery = usePermission('chargers.query');
  const canRegister = usePermission('chargers.register');
  const canEdit = usePermission('chargers.edit');
  const canEditSites = usePermission('sites.edit');
  const site = (useSites(undefined).data ?? []).find((s) => s.id === c.siteId);
  const removeCharger = useRemoveChargerFlow();
  const dialog = useDialog();
  const { run } = useCommand(c);
  const live = c.isConnected && canCommand;
  return (
    <RowMenu
      label={`Actions for ${chargerName(c)}`}
      items={[
        // Managers see the charger window read only, so for them it's Open.
        { label: canEdit ? 'Edit charger' : 'Open', icon: canEdit ? Pencil : Eye, onClick: onOpen },
        {
          label: 'Edit site',
          icon: Building2,
          hidden: !canEditSites || !site,
          onClick: () => void dialog.open(SiteDialog, { organisationId: site!.organisationId, organisationName: site!.organisationName, organisationTimezone: site!.timezone, site }),
        },
        {
          label: 'Restart',
          icon: RotateCcw,
          hidden: !live,
          onClick: async () => {
            if (await dialog.confirm({ title: `Restart ${chargerName(c)}?`, message: 'A soft restart: it finishes any session cleanly, then restarts. It is back in about a minute.', confirmLabel: 'Restart' })) {
              await run({ action: 'Reset', type: 'Soft' });
            }
          },
        },
        { label: 'Ask for its status', icon: Zap, hidden: !(c.isConnected && canQuery), onClick: () => void run({ action: 'TriggerMessage', requestedMessage: 'StatusNotification' }) },
        { label: 'Remove charger', icon: Trash2, danger: true, hidden: !canRegister, onClick: () => void removeCharger(c) },
      ]}
    />
  );
}

/** New chargers that connected for the first time (admins). */
function WaitingChargers({ chargers }: { chargers: Charger[] }) {
  const dialog = useDialog();
  const reject = useRejectCharger();
  const toast = useToast();
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, mb: 5, borderRadius: `${radius.card}px`, borderColor: colours.nearBlack }}>
      <Typography variant="h2" sx={{ mb: 0.5 }}>
        {chargers.length === 1 ? 'A new charger is waiting.' : `${chargers.length} new chargers are waiting.`}
      </Typography>
      <Typography color="textSecondary" sx={{ mb: 2.5 }}>
        They connected for the first time. Accept the ones you recognise and choose their site; until then they can't charge.
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {chargers.map((c) => (
          <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 1.5, borderTop: `1px solid ${colours.hairline}` }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 500 }} className="num">{c.chargePointId}</Typography>
              <Typography variant="body2" color="textSecondary">
                {[[c.vendor, c.model].filter(Boolean).join(' ') || 'Make not reported yet', c.registration === 'rejected' ? 'Rejected earlier' : null, `first seen ${formatAgo(c.createdAt)}`].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            {c.isConnected ? <StatusLabel tone="live">Connected</StatusLabel> : <StatusLabel tone="offline">Offline</StatusLabel>}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button size="small" onClick={() => void dialog.open(ChargerDialog, { chargerId: c.id, initialTab: 'messages' as const })}>Details</Button>
              {c.registration !== 'rejected' && (
                <Button
                  size="small"
                  onClick={async () => {
                    if (!(await dialog.confirm({ title: `Reject ${c.chargePointId}?`, message: 'It can keep connecting but never charge. You can still accept it later.', confirmLabel: 'Reject', danger: true }))) return;
                    try {
                      await reject.mutateAsync(c.id);
                      toast(`${c.chargePointId} was rejected.`);
                    } catch (e) {
                      toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
                    }
                  }}
                >
                  Reject
                </Button>
              )}
              <Button size="small" variant="contained" onClick={() => void dialog.open(AcceptChargerDialog, { charger: c })}>Accept</Button>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
